import { supabase } from '../client'
import type { UserIntentions, UsagePurpose, UserIntent } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── USER INTENTIONS ─────────────────────────────────────────── */

/**
 * Get user intentions
 */
export async function getUserIntentions(userId: string): Promise<UserIntentions | null> {
  if (!supabase) return null

  try {
    const { data, error} = await supabase
      .from('user_intentions')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()

    if (error) {
      logSupabaseError('Get intentions error', error)
      return null
    }

    return data
  } catch (error) {
    logSupabaseError('Get intentions error', error)
    return null
  }
}

export interface SaveUserIntentionsParams {
  usagePurposes?: UsagePurpose[]
  userIntents?: UserIntent[]
}

export interface SaveUserIntentionsResult {
  success: boolean
  error?: string
}

/**
 * Save or update user intentions
 * Supports partial updates - can save only usagePurposes, only userIntents, or both
 */
export async function saveUserIntentions(
  userId: string,
  params: SaveUserIntentionsParams
): Promise<SaveUserIntentionsResult> {
  if (!supabase) {
    return { success: false, error: 'Supabase not available' }
  }

  try {
    // Check if intentions already exist
    const existing = await getUserIntentions(userId)
    const now = new Date().toISOString()

    // Prepare update data with merge logic
    const updateData: any = {
      updated_at: now,
    }

    // Update usage_purposes if provided
    if (params.usagePurposes !== undefined) {
      updateData.usage_purposes = params.usagePurposes
      updateData.usage_purposes_updated_at = now
      if (!existing?.usage_purposes_completed_at) {
        updateData.usage_purposes_completed_at = now
      }
    }

    // Update user_intents if provided
    if (params.userIntents !== undefined) {
      updateData.user_intents = params.userIntents
      updateData.user_intents_updated_at = now
      if (!existing?.user_intents_completed_at) {
        updateData.user_intents_completed_at = now
      }
    }

    if (existing) {
      // Update existing record (partial update)
      const { error } = await supabase
        .from('user_intentions')
        .update(updateData)
        .eq('user_id', userId)

      if (error) {
        return { success: false, error: error.message }
      }
    } else {
      // Insert new record
      const { error } = await supabase
        .from('user_intentions')
        .insert({
          user_id: userId,
          ...updateData,
        })

      if (error) {
        return { success: false, error: error.message }
      }
    }

    return { success: true }
  } catch (error) {
    return { success: false, error: `Intentions save error: ${error}` }
  }
}
